import _ from 'lodash';
import { type VariantSnapshot } from '@bbkAdminComponents/experiences/pricing-pages-editors/hooks/useVariantPriceCache';
import {
  ExperienceCardTemplate,
  type AssembledExperience,
  type PricingPageHeaderCard,
} from '@bbkAdminRedux/rtkq/experience.slice';
import {
  type RestPricingPageJson,
  type RestPricingPageLayout,
} from '@bbkAdminRedux/rtkq/pricing-pages.slice';
import { type RestCard } from '@bbkAdminUtils/api-client/rest-experiences-api';
import { getTotalAmount } from '@bbkPricingPage/helpers/amount-calculator';
import { getFormattedAmount } from '@bbkPricingPage/lib/currencies';
import {
  ActionButtonType,
  BadgeType,
  GroupedBy,
  ItemType,
  PricingModel,
  QuantityMetaType,
  SubscriberCTAAction,
  type Addon,
  type Currency,
  type Frequency,
  type Item,
  type PeriodUnit,
  type Price,
  type PriceVariantKey,
  type PricingTable,
  type QuantityMeta,
  type Tier,
} from '@bbkPricingPage/types/pricing-table-types';

export type VariantItemPrices = {
  billing_system_id: string;
  prices: Price[];
};

export type VariantPlanItemPrices = VariantItemPrices & {
  period?: number | null;
  period_unit?: PeriodUnit | null;
};

export type VariantPayloadEntry = {
  variant_id: string;
  items: VariantPlanItemPrices[];
  addons: VariantItemPrices[];
};

type EntityWithFrequency = {
  period: number;
  period_unit: PeriodUnit;
};

export class PricingTableHelper {
  private static readonly PRICE_PERIOD_UNIT_MAPPING = {
    day: 'Daily',
    week: 'Weekly',
    month: 'Monthly',
    year: 'Yearly',
  };

  public static readonly PRICING_MODEL_MAPPING = {
    [PricingModel.flat_fee]: 'Flat Fee',
    [PricingModel.per_unit]: 'Per Unit',
    [PricingModel.tiered]: 'Tiered',
    [PricingModel.volume]: 'Volume',
    [PricingModel.stairstep]: 'Stairstep',
  };

  public static getPeriodPriceMapKey({
    period,
    period_unit,
  }: EntityWithFrequency): string {
    return `${period}::${period_unit}`;
  }

  public static getPeriodUnitTitle({
    period,
    period_unit,
  }: EntityWithFrequency): string {
    if (period === 1) {
      return PricingTableHelper.PRICE_PERIOD_UNIT_MAPPING[period_unit];
    } else {
      return `Every ${period} ${period_unit}s`;
    }
  }

  public static getPriceMap(
    prices: Price[] | undefined,
    currencies: Currency[] = [],
    frequencies: Frequency[] = []
  ): Map<PriceVariantKey, Map<string, Map<string, Price>>> {
    const priceMap = new Map<
      PriceVariantKey,
      Map<string, Map<string, Price>>
    >();

    const validCurrencies = new Set(
      currencies.filter((c) => c.is_attached).map((c) => c.currency_code)
    );
    const validFrequencies = new Set(
      frequencies
        .filter((f) => f.is_attached)
        .map((f) => f.id || `${f.period}::${f.period_unit}`)
    );

    prices?.forEach((price) => {
      if (
        !price.is_attached ||
        !validCurrencies.has(price.currency) ||
        !validFrequencies.has(`${price.period}::${price.period_unit}`)
      ) {
        return;
      }

      // Normalize undefined to null for variant ID
      const variantId =
        price.price_variant_id === undefined ? null : price.price_variant_id;

      const variantMap =
        priceMap.get(variantId) || new Map<string, Map<string, Price>>();
      const currencyMap =
        variantMap.get(price.currency) || new Map<string, Price>();
      const frequencyKey = `${price.period}::${price.period_unit}`;

      currencyMap.set(frequencyKey, price);
      variantMap.set(price.currency, currencyMap);
      priceMap.set(variantId, variantMap);
    });

    return priceMap;
  }

  public static hasAdditionalPrices(
    baseVariant?: Map<string, Map<string, Price>>,
    comparatorVariant?: Map<string, Map<string, Price>>
  ): boolean {
    if (!baseVariant || !comparatorVariant) {
      return false;
    }

    const comparatorPriceKeys = new Set<string>();
    for (const currencyMap of Array.from(comparatorVariant.values())) {
      for (const price of Array.from(currencyMap.values())) {
        comparatorPriceKeys.add(
          `${price.currency}::${price.period}::${price.period_unit}`
        );
      }
    }

    for (const currencyMap of Array.from(baseVariant.values())) {
      for (const price of Array.from(currencyMap.values())) {
        const priceKey = `${price.currency}::${price.period}::${price.period_unit}`;
        if (!comparatorPriceKeys.has(priceKey)) {
          return true;
        }
      }
    }

    return false;
  }

  public static generatePriceSuffix({
    period,
    period_unit,
  }: EntityWithFrequency): string {
    if (period === 1) {
      return `per ${period_unit}`;
    }
    return `per ${period} ${period_unit}`;
  }

  public static getUniquePeriodsFromPrices(
    prices: Price[] = [],
    frequencies: Frequency[] = []
  ): Frequency[] {
    const priceMap: Record<string, Frequency> = {};
    prices
      .filter((price) => price.is_attached === true)
      .forEach((price) => {
        if (!price.period || !price.period_unit) {
          return;
        }
        const key = PricingTableHelper.getPeriodPriceMapKey({
          period: price.period,
          period_unit: price.period_unit,
        });
        const existingFrequency = frequencies.find(
          (f) => PricingTableHelper.getPeriodPriceMapKey(f) === key
        ) as Frequency;
        priceMap[key] = {
          ...existingFrequency,
          id: key, // Set id to "period::period_unit" format
          title:
            existingFrequency?.title ??
            PricingTableHelper.getPeriodUnitTitle({
              period: price.period,
              period_unit: price.period_unit,
            }),
          period: price.period,
          period_unit: price.period_unit,
          is_attached: existingFrequency?.is_attached ?? false,
          display_order: existingFrequency?.display_order ?? 0,
          price_suffix:
            existingFrequency?.price_suffix ??
            PricingTableHelper.generatePriceSuffix({
              period: price.period,
              period_unit: price.period_unit,
            }),
          recurring_price_label: existingFrequency?.recurring_price_label ?? '',
          show_monthly_price: existingFrequency?.show_monthly_price ?? false,
        };
      });
    const result = Object.values(priceMap).sort(
      (item1, item2) =>
        (item1.display_order ?? -1) - (item2.display_order ?? -1)
    );
    return result;
  }

  public static getUniquePeriodsFromItems(
    items: Item[],
    frequencies: Frequency[] = []
  ): Frequency[] {
    return PricingTableHelper.getUniquePeriodsFromPrices(
      items
        .filter((item) => item.is_attached)
        .map((item) => item.prices || [])
        .flat(),
      frequencies
    );
  }

  public static getUniquePeriodsFromPricingTable(
    pricingTable: PricingTable
  ): Frequency[] {
    return PricingTableHelper.getUniquePeriodsFromItems(
      pricingTable.items,
      pricingTable.frequencies
    );
  }

  private static getUniqueCurrencyFromPrices(
    prices: Price[] = [],
    currencies: Currency[] = []
  ): Currency[] {
    const currencyMap: Record<string, Currency> = {};
    prices
      .filter((price) => price.is_attached === true)
      .forEach((price) => {
        const key = price.currency;
        const existingCurrency = currencies.find(
          (f) => f.currency_code === key
        );
        currencyMap[key] = {
          id: existingCurrency?.id || key,
          currency_code: key,
          is_attached: existingCurrency?.is_attached ?? false,
          display_order: existingCurrency?.display_order ?? 0,
        };
      });
    const result = Object.values(currencyMap).sort(
      (a, b) => a.display_order - b.display_order
    );
    return result;
  }

  public static getUniqueCurrenciesFromItems(
    items: Item[],
    currencies: Currency[] = []
  ): Currency[] {
    return PricingTableHelper.getUniqueCurrencyFromPrices(
      items
        .filter((item) => item.is_attached)
        .map((item) => item.prices || [])
        .flat(),
      currencies
    );
  }

  public static getUniqueCurrenciesFromPricingTable(
    pricingTable: PricingTable
  ): Currency[] {
    return PricingTableHelper.getUniqueCurrenciesFromItems(
      pricingTable.items,
      pricingTable.currencies
    );
  }

  public static isEligibleForMonthlyPrice(
    period: number,
    periodUnit: string
  ): boolean {
    return (period !== 1 && periodUnit === 'month') || periodUnit === 'year';
  }

  public static generatePriceSuffixForMonthlyPrice({
    period,
    period_unit,
  }: EntityWithFrequency): string {
    return (
      'per month billed ' +
      PricingTableHelper.getPeriodUnitTitle({ period, period_unit })
    );
  }

  public static getUniqueAddonIdsFromItems(items: Item[], itemType?: ItemType) {
    const uniqueAddonIds = new Set<string>(
      items
        .filter((item) => item.is_attached)
        .map((item) => item.attached_addons || [])
        .flat()
        .filter((addon) => !itemType || itemType === addon.item_type)
        .map((addon) => addon.billing_system_id)
    );
    return uniqueAddonIds;
  }

  public static getPriceElementDisplayText(price: Price) {
    let formattedAmount;
    let formattedStrikethroughAmount;
    let uomSuffix;
    let showStrikethrough = false;

    const format = (amount: number | string | null) => {
      if (amount === null) {
        return;
      }
      return getFormattedAmount(amount, price.currency);
    };

    if (
      price.pricing_model === PricingModel.flat_fee ||
      price.pricing_model === PricingModel.per_unit
    ) {
      const amount = getTotalAmount(price, 1, 'amount');
      const strikethroughAmount = getTotalAmount(
        price,
        1,
        'strikethrough_amount'
      );
      showStrikethrough = strikethroughAmount > amount;
      formattedAmount = format(amount);
      formattedStrikethroughAmount = format(strikethroughAmount);
    }
    if (
      (price.pricing_model === PricingModel.tiered ||
        price.pricing_model === PricingModel.volume ||
        price.pricing_model === PricingModel.stairstep) &&
      price.tiers
    ) {
      const minAmount = PricingTableHelper.getMinTierAmount(price.tiers);
      const maxAmount = PricingTableHelper.getMaxTierAmount(price.tiers);
      const minStrikethroughAmount = PricingTableHelper.getMinTierAmount(
        price.tiers,
        'strikethrough_amount'
      );
      const maxStrikethroughAmount = PricingTableHelper.getMaxTierAmount(
        price.tiers,
        'strikethrough_amount'
      );

      showStrikethrough =
        minStrikethroughAmount !== minAmount ||
        maxStrikethroughAmount !== maxAmount;

      formattedAmount = `${format(minAmount)}-${format(maxAmount)}`;
      formattedStrikethroughAmount =
        minStrikethroughAmount !== null
          ? `${format(minStrikethroughAmount)}-${format(maxStrikethroughAmount)}`
          : null;
    }

    if (
      price.pricing_model !== PricingModel.flat_fee &&
      price.pricing_model !== PricingModel.stairstep
    ) {
      uomSuffix = '/unit';
    }

    return {
      formattedAmount,
      formattedStrikethroughAmount: showStrikethrough
        ? formattedStrikethroughAmount
        : null,
      uomSuffix,
    };
  }

  public static getMinTierAmount(
    tiers: Tier[],
    key: 'amount' | 'strikethrough_amount' = 'amount'
  ): number | null {
    const amounts = tiers
      .filter((tier) => tier[key])
      .map((tier) => tier[key]) as number[];

    if (amounts !== null && amounts.length > 0) {
      return Math.min(...amounts);
    }
    return null;
  }

  public static getMaxTierAmount(
    tiers: Tier[],
    key: 'amount' | 'strikethrough_amount' = 'amount'
  ): number | null {
    const amounts = tiers
      .filter((tier) => tier[key])
      .map((tier) => tier[key]) as number[];

    if (amounts !== null && amounts.length > 0) {
      return Math.max(...amounts);
    }
    return null;
  }

  public static getUniqueCurrencyCodes(currencies: Currency[] = []): string[] {
    return currencies
      .filter((currency) => currency.is_attached)
      .map((currency) => currency.currency_code);
  }

  public static getUniqueFrequencyKeys(
    frequencies: Frequency[] = []
  ): string[] {
    return frequencies
      .filter((frequency) => frequency.is_attached)
      .map((frequency) => PricingTableHelper.getPeriodPriceMapKey(frequency));
  }

  public static getUniquePriceVariantsFromPrices(
    prices: Price[] | undefined,
    currencies: Currency[] = [],
    frequencies: Frequency[] = []
  ): string[] {
    if (!prices) return [];
    const attachedCurrencyCodes =
      PricingTableHelper.getUniqueCurrencyCodes(currencies);
    const attachedFrequencyKeys =
      PricingTableHelper.getUniqueFrequencyKeys(frequencies);

    const filteredPrices = prices.filter(
      (price) =>
        price.is_attached &&
        attachedCurrencyCodes.includes(price.currency) &&
        (price.period && price.period_unit
          ? attachedFrequencyKeys.includes(
              PricingTableHelper.getPeriodPriceMapKey({
                ...price,
                period: price.period,
                period_unit: price.period_unit,
              })
            )
          : true)
    );

    const uniquePriceVariantIds = new Set(
      filteredPrices
        .map((price) =>
          price.price_variant_id === undefined ? null : price.price_variant_id
        )
        .sort((a, b) => (a ?? '').localeCompare(b ?? ''))
    );

    return Array.from(uniquePriceVariantIds) as string[];
  }

  public static getUniquePriceVariantsFromItems(
    items: Item[] | undefined,
    currencies: Currency[] = [],
    frequencies: Frequency[] = []
  ): string[] {
    if (!items) return [];

    const filteredItems = items.filter((item) => item.is_attached);
    const prices = filteredItems.map((item) => item.prices).flat();
    return PricingTableHelper.getUniquePriceVariantsFromPrices(
      prices,
      currencies,
      frequencies
    );
  }

  public static getUniquePriceVariantsFromPricingTable(
    pricingTable: PricingTable | undefined
  ): string[] {
    if (!pricingTable) return [];

    return PricingTableHelper.getUniquePriceVariantsFromItems(
      pricingTable.items,
      pricingTable.currencies,
      pricingTable.frequencies
    );
  }
  public static isItemHavingNonFlatFeeAndAttachedCurrencyAndFrequencyPrice = (
    item: Item,
    currencies: Currency[],
    frequencies: Frequency[]
  ) => {
    return (
      item?.prices?.filter(
        (price) =>
          price.pricing_model !== PricingModel.flat_fee &&
          currencies.some(
            (currency) =>
              currency.currency_code === price.currency && currency.is_attached
          ) &&
          frequencies.some(
            (frequency) =>
              frequency.period === price.period &&
              frequency.period_unit === price.period_unit &&
              frequency.is_attached
          )
      ).length > 0
    );
  };

  public static getUniqueProductFamiliesFromItems(items: Item[]): string[] {
    const uniqueFamilies = new Set<string>();
    for (const item of items) {
      if (item.product_family) {
        uniqueFamilies.add(item.product_family);
      }
    }
    return Array.from(uniqueFamilies);
  }

  public static getMostUsedProductFamily(pricingTableItems: Item[]): string {
    const selectedProductFamilyList = pricingTableItems.map((item) => {
      if (item.is_attached) {
        return item.product_family;
      }
    });
    const countMap = selectedProductFamilyList.reduce(
      (map: { [key: string]: number }, currentString: string | undefined) => {
        if (currentString !== undefined) {
          map[currentString] = (map[currentString] || 0) + 1;
        }
        return map;
      },
      {}
    );

    const mostOccurringString = Object.keys(countMap).reduce(
      (a, b) => (countMap[a]! > countMap[b]! ? a : b),
      pricingTableItems[0]?.product_family ?? ''
    );

    return mostOccurringString;
  }

  public static getPricingTableFromLayout(
    layout: RestPricingPageLayout
  ): PricingTable {
    return {
      ..._.get(layout, 'pricing_table.pricing_element'),
      id: _.get(layout, 'pricing_table.id'),
      name: _.get(layout, 'pricing_table.name'),
      variant_ids: layout.variant_ids,
    } as PricingTable;
  }

  public static getAssembledExperienceFromRestPricingPageJson(
    json: RestPricingPageJson
  ): AssembledExperience {
    return _.set(
      _.cloneDeep(json?.experience),
      'layout.pricing_table',
      PricingTableHelper.getPricingTableFromLayout(json?.experience.layout)
    ) as unknown as AssembledExperience;
  }

  /**
   * Formats a list of frequencies by ensuring they have default values
   * and are sorted by display order
   */
  public static getFormattedFrequencies(
    frequencies: Frequency[] = []
  ): Frequency[] {
    return [...frequencies]
      .sort((a, b) => (a.display_order ?? -1) - (b.display_order ?? -1))
      .map((frequency, index) => {
        const priceSuffix =
          frequency.price_suffix !== null &&
          frequency.price_suffix !== undefined
            ? frequency.price_suffix
            : PricingTableHelper.generatePriceSuffix({
                period: frequency.period,
                period_unit: frequency.period_unit,
              });
        const title =
          frequency.title !== null && frequency.title !== undefined
            ? frequency.title
            : PricingTableHelper.getPeriodUnitTitle({
                period: frequency.period,
                period_unit: frequency.period_unit,
              });
        return {
          ...frequency,
          title,
          show_monthly_price: frequency.show_monthly_price ?? false,
          price_suffix: priceSuffix,
          display_order: index,
          recurring_price_label:
            frequency.recurring_price_label ??
            `Renews at {{price}} ${priceSuffix}`,
        };
      });
  }

  public static itemsComparator(
    item1: Item | Currency,
    item2: Item | Currency
  ) {
    if (item1.is_attached && !item2.is_attached) return -1;
    if (!item1.is_attached && item2.is_attached) return 1;
    // add item in the end if it doesn't have displayOrder
    return (
      (item1.display_order ?? Number.MAX_VALUE) -
      (item2.display_order ?? Number.MAX_VALUE)
    );
  }

  public static isItemHavingNonFlatFeePrice(item: Item): boolean {
    return (
      item?.prices?.filter(
        (price) => price.pricing_model !== PricingModel.flat_fee
      ).length > 0
    );
  }

  public static getQuantityMeta(
    pricingModel: PricingModel,
    tiers?: Tier[],
    existingQuantityMeta?: QuantityMeta
  ): QuantityMeta | undefined {
    if (pricingModel === PricingModel.flat_fee) {
      return undefined;
    }

    let min = existingQuantityMeta?.min;
    let max = existingQuantityMeta?.max;
    let step = existingQuantityMeta?.step;

    if (pricingModel !== PricingModel.per_unit) {
      // tiered, stairstep, volume
      if (tiers) {
        const newMin = Math.min(...tiers.map((tier) => tier.starting_unit));
        if (!min || newMin > min) {
          min = newMin;
        }
        const newMax = Math.max(
          ...tiers.map((tier) => tier.ending_unit ?? tier.starting_unit)
        );
        if (!max) {
          max = newMax;
        }
        if (max < min) {
          max = min;
        }
      }
    }

    min = min ?? 1;
    max = max ?? 10;
    step = step ?? 1;

    let defaultValue = existingQuantityMeta?.default_value ?? min;
    if (defaultValue < min) {
      defaultValue = min;
    }
    if (defaultValue > max) {
      defaultValue = max;
    }

    return {
      ...existingQuantityMeta,
      min,
      max,
      step,
      default_value: defaultValue,
      type: existingQuantityMeta?.type ?? QuantityMetaType.NONE,
    } as QuantityMeta;
  }

  public static getAllTierLinesInItem(item: Item): Tier[] {
    let tiers: Tier[] = [];
    if (PricingTableHelper.isItemHavingNonFlatFeePrice(item)) {
      item?.prices
        ?.filter(
          (price) =>
            price.pricing_model !== PricingModel.flat_fee && price.tiers
        )
        .map((price) => {
          tiers = tiers.concat(price.tiers as Tier[]);
        });
    }
    return tiers;
  }

  public static getFormattedItem(
    item: Item,
    groupedBy?: string,
    index?: number
  ): Item {
    const defaultFeatures = `<div>What's included</div><ul class="slate-ul"><li class="slate-li"><div class="slate-p">Feature 1</div></li><li class="slate-li"><div class="slate-p">Feature 2</div></li><li class="slate-li"><div class="slate-p">Feature 3</div></li></ul>`;
    const tiers: Tier[] = PricingTableHelper.getAllTierLinesInItem(item);
    const dummyPricingModel =
      tiers.length > 0 ? PricingModel.tiered : PricingModel.per_unit;

    const formattedItem = {
      ...item,
      customization: {
        ...item.customization,
        display_name:
          groupedBy === 'item_frequency'
            ? (item.customization?.display_name ?? item.customization?.name)
            : item.customization?.display_name,
        description: item.customization?.description ?? '',
        badge: {
          text: item.customization?.badge?.text ?? 'Most Popular',
          type: item.customization?.badge?.type ?? BadgeType.none,
        },
        action_button: {
          ...item.customization?.action_button,
          text: item.customization?.action_button?.text ?? 'Choose Plan',
          type:
            item.customization?.action_button?.type ??
            ActionButtonType.chargebee_checkout,
        },
        features: item.customization?.features ?? defaultFeatures,
        info_text: item.customization?.info_text,
        highlight: item.customization?.highlight ?? false,
        is_custom_amount_text:
          item.customization?.is_custom_amount_text ?? false,
        custom_amount_text: item.customization?.custom_amount_text,
        custom_amount_sub_text: item.customization?.custom_amount_sub_text,
        quantity_meta: PricingTableHelper.isItemHavingNonFlatFeePrice(item)
          ? PricingTableHelper.getQuantityMeta(
              dummyPricingModel,
              tiers,
              item.customization?.quantity_meta
            )
          : undefined,
      },
      display_order: index ?? item.display_order,
      attached_addons: item.attached_addons ?? [],
      prices:
        item.prices?.map((price) => ({
          ...price,
          active: price.active ?? true,
          differential_prices: price.differential_prices ?? [],
        })) ?? [],
    };
    return formattedItem;
  }

  public static getFormattedItems(
    items: Item[] = [],
    groupedBy?: string
  ): Item[] {
    return [...items]
      .sort(PricingTableHelper.itemsComparator)
      .map((item, index) =>
        PricingTableHelper.getFormattedItem(item, groupedBy, index + 1)
      );
  }

  public static addonsComparator(
    addon1: Addon,
    addon2: Addon,
    applicableAddons: Set<string>
  ): number {
    if (addon1.item_type !== addon2.item_type) {
      return addon1.item_type === ItemType.addon ? -1 : 1;
    }

    const addon1Id = addon1.billing_system_id.toLowerCase();
    const addon2Id = addon2.billing_system_id.toLowerCase();

    const isAddon1Applicable = applicableAddons.has(addon1Id);
    const isAddon2Applicable = applicableAddons.has(addon2Id);

    if (isAddon1Applicable && !isAddon2Applicable) {
      return -1;
    }
    if (!isAddon1Applicable && isAddon2Applicable) {
      return 1;
    }

    return (addon1.prices?.length ?? 0) - (addon2.prices?.length ?? 0);
  }

  public static getFormattedAddons(
    addons: Addon[] = [],
    items: Item[] = []
  ): Addon[] {
    const applicableAddons =
      PricingTableHelper.getUniqueAddonIdsFromItems(items);
    return [...addons].sort((addon1, addon2) =>
      PricingTableHelper.addonsComparator(addon1, addon2, applicableAddons)
    );
  }

  public static getFormattedCurrencies(
    currencies: Currency[] = []
  ): Currency[] {
    return [...currencies]
      .sort((a, b) => (a.display_order ?? -1) - (b.display_order ?? -1))
      .map((currency, index) => ({
        ...currency,
        display_order: index,
      }));
  }

  public static getFormattedPricingTable(
    pricingTable: PricingTable
  ): PricingTable {
    return {
      ...pricingTable,
      grouped_by: pricingTable.grouped_by ?? 'item',
      frequencies: PricingTableHelper.getFormattedFrequencies(
        pricingTable.frequencies
      ),
      currencies: [...(pricingTable.currencies ?? [])]
        .sort(PricingTableHelper.itemsComparator)
        .map((currency, index) => ({
          ...currency,
          display_order: index,
        })),
      items: PricingTableHelper.getFormattedItems(
        pricingTable.items,
        pricingTable.grouped_by
      ),
      addons: PricingTableHelper.getFormattedAddons(
        pricingTable.addons,
        pricingTable.items
      ),
      paywall_config: {
        ...pricingTable.paywall_config,
        hide_subscribed_plan:
          pricingTable.paywall_config?.hide_subscribed_plan ?? false,
        hide_frequency_switch:
          pricingTable.paywall_config?.hide_frequency_switch ?? false,
        action_button: {
          ...pricingTable.paywall_config?.action_button,
          text:
            pricingTable.paywall_config?.action_button?.text ??
            "You're currently on this plan!",
          action:
            pricingTable.paywall_config?.action_button?.action ??
            SubscriberCTAAction.do_nothing,
        },
      },
      customization: {
        ...pricingTable.customization,
        plan_customization: {
          ...pricingTable.customization?.plan_customization,
          heading:
            pricingTable.customization?.plan_customization?.heading ??
            'Personalize your plan',
          sub_heading:
            pricingTable.customization?.plan_customization?.sub_heading ??
            'See what’s included in your plan and personalize it with add-ons',
          mandatory_addon_title:
            pricingTable.customization?.plan_customization
              ?.mandatory_addon_title ?? 'Included with your plan',
          recommended_addon_title:
            pricingTable.customization?.plan_customization
              ?.recommended_addon_title ?? 'Customize your plan',
          // Seeded as '' rather than the default copy so that clearing the
          // field returns the form to its pristine baseline. The renderer owns
          // the fallback text; see resolveAddonSummaryLabel.
          addon_summary_label:
            pricingTable.customization?.plan_customization
              ?.addon_summary_label ?? '',
        },
        show_addons_and_charges:
          pricingTable.customization?.show_addons_and_charges ?? false,
        hide_price_breakdown:
          pricingTable.customization?.hide_price_breakdown ?? false,
        hide_mandatory_addon_prices:
          pricingTable.customization?.hide_mandatory_addon_prices ?? false,
        hide_plan_customization:
          pricingTable.customization?.hide_plan_customization ?? false,
      },
    };
  }

  /**
   * Builds the cards and pricing table payload for saving/publishing a pricing page.
   * This consolidates the repeated payload construction logic used across save, publish, and ungroup operations.
   */
  public static buildPricingPagePayload(
    header: { title: string; details: string },
    pricingTable: PricingTable
  ): {
    cards: RestCard[];
    pricingTable: {
      id: string;
      name: string;
      version: string;
      template: string;
      pricing_element: Pick<
        PricingTable,
        | 'grouped_by'
        | 'frequencies'
        | 'currencies'
        | 'items'
        | 'addons'
        | 'customization'
        | 'paywall_config'
      >;
    };
  } {
    const cards: RestCard[] = [
      {
        id: 'header-card',
        name: 'Header Card',
        version: '1.0',
        type: 'Card' as const,
        deploy: 'live',
        template: ExperienceCardTemplate.simple_header,
        requires: {
          header,
        },
        style_overlay: {},
      } as PricingPageHeaderCard,
    ];

    // Deduplicate by (billing_system_id, period, period_unit); prefer is_attached entries.
    const deduplicateItems = (items: Item[]): Item[] => {
      const seen = new Map<string, Item>();
      items.forEach((item) => {
        const key = `${item.billing_system_id}::${item.period ?? ''}::${item.period_unit ?? ''}`;
        const existing = seen.get(key);
        if (!existing || item.is_attached) {
          seen.set(key, item);
        }
      });
      return Array.from(seen.values());
    };

    const pricingTablePayload = {
      id: 'pricing-table',
      name: 'Pricing Table',
      version: '1.0',
      template: 'pricing_table',
      pricing_element: {
        grouped_by: pricingTable.grouped_by,
        frequencies: pricingTable.frequencies,
        currencies: pricingTable.currencies,
        items: deduplicateItems(pricingTable.items),
        addons: pricingTable.addons,
        customization: pricingTable.customization,
        paywall_config: pricingTable.paywall_config,
      },
    };

    return { cards, pricingTable: pricingTablePayload };
  }

  private static isPriceOverridden(price: Price): boolean {
    if (price.strikethrough_amount != null) return true;
    if (price.amount != null && price.amount !== price.billing_system_amount) {
      return true;
    }

    const tiers = price.tiers ?? [];
    if (tiers.length === 0) return false;

    const billingTiers = price.billing_system_tiers ?? [];
    return tiers.some((tier, index) => {
      if (tier.strikethrough_amount != null) return true;
      const billingTier = billingTiers[index];
      if (!billingTier) return false;
      return tier.amount !== billingTier.amount;
    });
  }

  private static variantHasOverriddenPrices(
    snapshot: VariantSnapshot,
    variantId: string
  ): boolean {
    const matchesVariant = (price: Price) =>
      variantId === 'default'
        ? price.price_variant_id == null
        : price.price_variant_id === variantId;

    const anyOverride = (prices: Price[] | undefined) =>
      (prices ?? [])
        .filter(matchesVariant)
        .some((p) => PricingTableHelper.isPriceOverridden(p));

    return (
      snapshot.items.some((item) => anyOverride(item.prices)) ||
      snapshot.addons.some((addon) => anyOverride(addon.prices))
    );
  }

  /** Splits each item into one row per (period, period_unit). */
  public static groupItemsByFrequency(
    items: Item[],
    frequencies: Frequency[],
    maxAttachedItems: number = 5
  ): Item[] {
    const groupedItems: Item[] = [];
    let attachedCounter = 0;

    for (const item of items) {
      const pricesByFrequency = new Map<string, Price[]>();
      for (const price of item.prices ?? []) {
        const frequencyKey = `${price.period}::${price.period_unit}`;
        if (!pricesByFrequency.has(frequencyKey)) {
          pricesByFrequency.set(frequencyKey, []);
        }
        pricesByFrequency.get(frequencyKey)!.push(price);
      }

      for (const [frequencyKey, prices] of pricesByFrequency.entries()) {
        const [period, periodUnit] = frequencyKey.split('::');
        if (!period || !periodUnit) continue;

        const isFrequencyAttached = frequencies.some(
          (freq) =>
            freq.period === parseInt(period) &&
            freq.period_unit === periodUnit &&
            freq.is_attached
        );
        const isPriceAttached = prices.some((p) => p.is_attached);

        const shouldAttach =
          item.is_attached &&
          isFrequencyAttached &&
          isPriceAttached &&
          attachedCounter < maxAttachedItems;

        if (shouldAttach) attachedCounter++;

        groupedItems.push({
          ...item,
          customization: {
            ...item.customization,
            display_name:
              item.customization.display_name || item.customization.name,
          },
          period: parseInt(period),
          period_unit: periodUnit as PeriodUnit,
          prices,
          is_attached: shouldAttach,
        });
      }
    }

    return groupedItems;
  }

  /**
   * Build the V2 save payload. Item/addon prices are stripped from
   * `pricing_table` and placed in `variants[]` keyed by variant_id.
   * Only dirty variants (touched this session) are included; the cache
   * provides snapshots for inactive variants.
   */
  public static buildPricingPagePayloadV2(
    header: { title: string; details: string },
    pricingTable: PricingTable,
    activeVariantId: string,
    cache: Map<string, VariantSnapshot>,
    dirtyVariantIds: ReadonlySet<string> = new Set(),
    includeVariantsWithOverriddenPrices: boolean = false
  ) {
    const cards: RestCard[] = [
      {
        id: 'header-card',
        name: 'Header Card',
        version: '1.0',
        type: 'Card' as const,
        deploy: 'live',
        template: ExperienceCardTemplate.simple_header,
        requires: {
          header,
        },
        style_overlay: {},
      } as PricingPageHeaderCard,
    ];

    // Strip prices from items/addons — prices go into the variants array
    const itemsWithoutPrices = pricingTable.items.map(
      ({ prices, ...item }) => item
    );
    const addonsWithoutPrices = pricingTable.addons.map(
      ({ prices, ...addon }) => addon
    );

    const pricingTablePayload = {
      id: pricingTable.id || 'pricing-table',
      name: pricingTable.name || 'Pricing Table',
      version: '1.0',
      template: 'pricing_table',
      pricing_element: {
        grouped_by: pricingTable.grouped_by,
        frequencies: pricingTable.frequencies,
        currencies: pricingTable.currencies,
        items: itemsWithoutPrices,
        addons: addonsWithoutPrices,
        customization: pricingTable.customization,
        paywall_config: pricingTable.paywall_config,
      },
    };

    const rawVariantIds = pricingTable.variant_ids ?? [];
    const variant_ids = rawVariantIds.includes('default')
      ? ['default', ...rawVariantIds.filter((id) => id !== 'default')]
      : rawVariantIds;

    // Always use the live form for the active variant (cache may be stale).
    const allSnapshots = new Map(cache);
    allSnapshots.set(activeVariantId, {
      items: pricingTable.items,
      addons: pricingTable.addons,
      frequencies: pricingTable.frequencies,
      currencies: pricingTable.currencies,
    });

    // Default-variant prices have no price_variant_id; non-default must match exactly.
    const filterPricesForVariant = (prices: Price[], variantId: string) =>
      prices.filter((p) =>
        variantId === 'default'
          ? p.price_variant_id == null
          : p.price_variant_id === variantId
      );

    const includeItemFrequency =
      pricingTable.grouped_by === GroupedBy.item_frequency;

    const variants: VariantPayloadEntry[] = variant_ids
      .map((key) => {
        const snap = allSnapshots.get(key);
        if (!snap) return null;
        const shouldInclude =
          dirtyVariantIds.has(key) ||
          (includeVariantsWithOverriddenPrices &&
            PricingTableHelper.variantHasOverriddenPrices(snap, key));
        if (!shouldInclude) return null;
        return {
          variant_id: key,
          items: snap.items.map((item) => ({
            billing_system_id: item.billing_system_id,
            ...(includeItemFrequency && {
              period: item.period ?? null,
              period_unit: item.period_unit ?? null,
            }),
            prices: filterPricesForVariant(item.prices ?? [], key),
          })),
          addons: snap.addons.map((addon) => ({
            billing_system_id: addon.billing_system_id,
            prices: filterPricesForVariant(addon.prices ?? [], key),
          })),
        };
      })
      .filter((v): v is VariantPayloadEntry => v !== null);

    return { cards, pricingTable: pricingTablePayload, variant_ids, variants };
  }

  /**
   * V2 ungroup-by-frequency payload. Ungroups each freshly fetched variant
   * table, picks up to `maxItems` attached `(item, period, period_unit)`
   * keys across the union (active variant first), and emits per-variant
   * prices for those keys. Variants with no priced rows are dropped.
   */
  public static buildUngroupedPricingPagePayloadV2(
    header: { title: string; details: string },
    originalPricingTable: PricingTable,
    variantTables: Map<string, PricingTable>,
    activeVariantId: string,
    maxItems: number = 5
  ) {
    const itemKey = (i: {
      billing_system_id: string;
      period?: number | null;
      period_unit?: PeriodUnit | null;
    }) => `${i.billing_system_id}::${i.period ?? ''}::${i.period_unit ?? ''}`;

    const ungroupedByVariant = new Map<string, Item[]>();
    variantTables.forEach((vt, vid) => {
      ungroupedByVariant.set(
        vid,
        PricingTableHelper.groupItemsByFrequency(
          vt.items ?? [],
          vt.frequencies ?? [],
          Number.POSITIVE_INFINITY
        )
      );
    });

    const orderedVariantIds = [
      activeVariantId,
      ...Array.from(variantTables.keys()).filter((v) => v !== activeVariantId),
    ];

    const pickedKeys: string[] = [];
    const seenKeys = new Set<string>();
    const fallbackItemByKey = new Map<string, Item>();

    for (const vid of orderedVariantIds) {
      if (pickedKeys.length >= maxItems) break;
      const items = ungroupedByVariant.get(vid) ?? [];
      for (const item of items) {
        if (!item.is_attached) continue;
        const k = itemKey(item);
        if (seenKeys.has(k)) continue;
        if (pickedKeys.length >= maxItems) break;
        seenKeys.add(k);
        pickedKeys.push(k);
        fallbackItemByKey.set(k, item);
      }
    }

    const activeItemByKey = new Map<string, Item>();
    (ungroupedByVariant.get(activeVariantId) ?? []).forEach((i) =>
      activeItemByKey.set(itemKey(i), i)
    );

    const finalItems: Item[] = pickedKeys.map((k, index) => {
      const meta = activeItemByKey.get(k) ?? fallbackItemByKey.get(k)!;
      return {
        ...meta,
        is_attached: true,
        display_order: index + 1,
      };
    });

    const cards: RestCard[] = [
      {
        id: 'header-card',
        name: 'Header Card',
        version: '1.0',
        type: 'Card' as const,
        deploy: 'live',
        template: ExperienceCardTemplate.simple_header,
        requires: { header },
        style_overlay: {},
      } as PricingPageHeaderCard,
    ];

    const activeTable =
      variantTables.get(activeVariantId) ?? originalPricingTable;

    const itemsWithoutPrices = finalItems.map(({ prices, ...item }) => item);
    const addonsWithoutPrices = (activeTable.addons ?? []).map(
      ({ prices, ...addon }) => addon
    );

    const pricingTablePayload = {
      id: originalPricingTable.id || 'pricing-table',
      name: originalPricingTable.name || 'Pricing Table',
      version: '1.0',
      template: 'pricing_table',
      pricing_element: {
        grouped_by: GroupedBy.item_frequency,
        frequencies: activeTable.frequencies,
        currencies: activeTable.currencies,
        items: itemsWithoutPrices,
        addons: addonsWithoutPrices,
        customization: activeTable.customization,
        paywall_config: activeTable.paywall_config,
      },
    };

    const filterPricesForVariant = (prices: Price[], variantId: string) =>
      prices.filter((p) =>
        variantId === 'default'
          ? p.price_variant_id == null
          : p.price_variant_id === variantId
      );

    const allVariants: VariantPayloadEntry[] = orderedVariantIds.map((vid) => {
      const ungrouped = ungroupedByVariant.get(vid) ?? [];
      const variantItems = ungrouped
        .filter((i) => seenKeys.has(itemKey(i)))
        .map((item) => ({
          billing_system_id: item.billing_system_id,
          period: item.period ?? null,
          period_unit: item.period_unit ?? null,
          prices: filterPricesForVariant(item.prices ?? [], vid),
        }));
      const variantAddons = (variantTables.get(vid)?.addons ?? []).map(
        (addon) => ({
          billing_system_id: addon.billing_system_id,
          prices: filterPricesForVariant(addon.prices ?? [], vid),
        })
      );
      return {
        variant_id: vid,
        items: variantItems,
        addons: variantAddons,
      };
    });

    const survivingVariants = allVariants.filter((v) =>
      v.items.some((i) => (i.prices?.length ?? 0) > 0)
    );

    const survivingIds = new Set(survivingVariants.map((v) => v.variant_id));
    const rawVariantIds =
      originalPricingTable.variant_ids ?? Array.from(variantTables.keys());
    const reorderedVariantIds = rawVariantIds.filter((id) =>
      survivingIds.has(id)
    );
    const finalVariantIds = reorderedVariantIds.includes('default')
      ? ['default', ...reorderedVariantIds.filter((id) => id !== 'default')]
      : reorderedVariantIds;

    return {
      cards,
      pricingTable: pricingTablePayload,
      variant_ids: finalVariantIds,
      variants: survivingVariants,
    };
  }
}

export default PricingTableHelper;
